import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  ChevronLeftIcon,
  IconButton,
  Input,
  MessageBubble,
  SendIcon,
  VerifiedBadgeIcon,
  cn,
} from '@openroad/ui';
import { api, formatDay, formatTime, unwrap } from '../lib/api';
import { useEndSessionOnAuthFailure } from '../lib/session';
import { useIsMobileLayout } from '../lib/viewport';

/**
 * Thread list and conversation, side by side as the export draws them.
 *
 * The selected thread lives in the URL — `/messages/:id` — rather than in
 * component state, so a conversation can be linked to. That is what the
 * "Message driver" button and every message notification point at.
 *
 * On a phone the two panes take turns instead: a 320px list beside a
 * conversation leaves the conversation about twenty pixels wide. Because the
 * selection is already in the URL, one pane per screen needs no new state —
 * `/messages` is the list, `/messages/:id` is the conversation, and Back is a
 * link to the former (ADR-0010).
 */

/** "12m", "2h", "Yesterday" — the relative stamp the export's thread list uses. */
function relativeTime(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000);

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h`;
  if (minutes < 48 * 60) return 'Yesterday';
  return formatDay(iso);
}

export function MessagesScreen() {
  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mobile = useIsMobileLayout();

  const threads = useQuery({
    queryKey: ['threads'],
    queryFn: () => unwrap(api.GET('/threads')),
  });

  const conversation = useQuery({
    queryKey: ['thread', selectedId],
    queryFn: () => unwrap(api.GET('/threads/{id}', { params: { path: { id: selectedId! } } })),
    enabled: Boolean(selectedId),
  });

  const expired = useEndSessionOnAuthFailure(threads.error, conversation.error);

  const [draft, setDraft] = useState('');
  const endOfThread = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (body: string) =>
      unwrap(
        api.POST('/threads/{id}/messages', {
          params: { path: { id: selectedId! } },
          body: { body },
        }),
      ),
    onSuccess: async () => {
      setDraft('');
      // Both: the conversation gains the message, and the list's preview and
      // ordering move with it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['thread', selectedId] }),
        queryClient.invalidateQueries({ queryKey: ['threads'] }),
      ]);
    },
  });

  // A conversation opens at its newest message, which is where a reader expects
  // to be — not at the top of a year of history.
  useEffect(() => {
    endOfThread.current?.scrollIntoView({ block: 'end' });
  }, [conversation.data?.messages.length, selectedId]);

  if (expired) return null;

  const items = threads.data?.items ?? [];
  const active = conversation.data;

  function submit(event: FormEvent) {
    event.preventDefault();
    const body = draft.trim();
    if (body === '' || send.isPending) return;
    send.mutate(body);
  }

  // One pane at a time on a phone; both, side by side, on anything wider.
  const showList = !mobile || !selectedId;
  const showConversation = !mobile || Boolean(selectedId);

  return (
    <main
      className={cn(
        'mx-auto max-w-[1000px]',
        mobile
          ? // The header, the tab bar and whatever the device reserves at each
            // end. `dvh` rather than `vh` because a phone browser's toolbars
            // move, and a chat whose composer is under one is not a chat.
            'h-[calc(100dvh-4rem-var(--height-tab-bar)-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] px-3 pt-3'
          : 'h-[calc(100vh-64px)] px-6 pt-5',
      )}
    >
      <Card
        radius="panel"
        className={cn(
          'grid h-full overflow-hidden p-0',
          showList && showConversation ? 'grid-cols-[320px_1fr]' : 'grid-cols-1',
        )}
      >
        {/* thread list */}
        {showList ? (
          <div className={cn('flex flex-col', !mobile && 'border-r border-line-soft')}>
            <div className="px-[18px] pt-[18px] pb-3">
              <h1 className="m-0 font-display text-title font-bold">Messages</h1>
            </div>

            <div className="flex-1 overflow-y-auto">
              {threads.isPending ? (
                <p className="px-[18px] text-meta text-muted">Loading…</p>
              ) : threads.isError ? (
                <p className="px-[18px] text-meta text-muted">
                  We could not load your conversations.
                </p>
              ) : items.length === 0 ? (
                <p className="px-[18px] text-meta text-muted">
                  No conversations yet. Message a driver from their ride and it will appear here.
                </p>
              ) : (
                items.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => navigate(`/messages/${thread.id}`)}
                    aria-current={thread.id === selectedId ? 'true' : undefined}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-3 border-0 border-b border-line-faint px-[18px] py-[13px] text-left',
                      thread.id === selectedId ? 'bg-hover' : 'bg-transparent hover:bg-hover-soft',
                    )}
                  >
                    <Avatar
                      initials={thread.with?.initials ?? '··'}
                      colour={thread.with?.avatarColour ?? '#8A938C'}
                      size={42}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex justify-between">
                        <span className="text-body font-bold">
                          {thread.with?.displayName ?? 'Smolit'}
                        </span>
                        <span className="text-mini text-subtle">
                          {relativeTime(thread.lastMessageAt)}
                        </span>
                      </span>
                      <span className="block truncate text-meta text-muted">
                        {thread.lastMessage?.body ?? 'No messages yet'}
                      </span>
                    </span>
                    {thread.unread ? (
                      <span
                        className="h-2 w-2 flex-none rounded-full bg-pohutukawa"
                        aria-label="Unread"
                      />
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}

        {/* conversation */}
        {showConversation ? (
          <div className="flex flex-col">
            {!selectedId ? (
              <div className="flex flex-1 items-center justify-center px-6 text-center">
                <p className="text-base text-muted">Pick a conversation to read it.</p>
              </div>
            ) : conversation.isPending ? (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-base text-muted">Loading…</p>
              </div>
            ) : conversation.isError || !active ? (
              <div className="flex flex-1 items-center justify-center px-6 text-center">
                <p className="text-base text-muted">We could not open that conversation.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 border-b border-line-soft px-5 py-3.5 max-md:px-3">
                  {/* The way back to the list, which on a phone is the screen this
                    one replaced rather than a pane beside it. */}
                  {mobile ? (
                    <IconButton
                      label="Back to messages"
                      size="sm"
                      shape="square"
                      onClick={() => navigate('/messages')}
                    >
                      <ChevronLeftIcon size={16} />
                    </IconButton>
                  ) : null}
                  <Avatar
                    initials={active.thread.with?.initials ?? '··'}
                    colour={active.thread.with?.avatarColour ?? '#8A938C'}
                    size={40}
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-base font-bold">
                      {active.thread.with?.displayName ?? 'Smolit'}
                      {(active.thread.with?.verifiedBadges.length ?? 0) > 0 ? (
                        <VerifiedBadgeIcon size={14} />
                      ) : null}
                    </div>
                    {active.thread.trip ? (
                      <div className="text-caption text-muted">
                        {active.thread.trip.origin} → {active.thread.trip.destination} ·{' '}
                        {formatDay(active.thread.trip.departureAt)}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto bg-sunk p-5">
                  {active.messages.map((message) => (
                    <MessageBubble
                      key={message.id}
                      author={message.mine ? 'me' : 'them'}
                      timestamp={formatTime(message.sentAt)}
                    >
                      {message.body}
                    </MessageBubble>
                  ))}
                  <div ref={endOfThread} />
                </div>

                <form
                  onSubmit={submit}
                  className="flex items-center gap-2.5 border-t border-line-soft px-5 py-3.5"
                >
                  <Input
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={`Message ${active.thread.with?.displayName.split(' ')[0] ?? ''}…`}
                    aria-label="Write a message"
                    className="flex-1"
                    maxLength={2000}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    aria-label="Send"
                    className="h-11 w-11 flex-none px-0"
                    disabled={draft.trim() === '' || send.isPending}
                  >
                    <SendIcon size={18} />
                  </Button>
                </form>
                {send.isError ? (
                  <p className="px-5 pb-3 text-meta text-danger" role="alert">
                    That message did not send. Try again.
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </Card>
    </main>
  );
}
